var _excluded = ["children", "className", "variant", "element", "utils", "isInverse", "hasBullets", "weight", "isItalic"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useContext, createContext, useMemo, useState, useCallback, useEffect } from 'react';
import InverseContext from '../../utilities/inverse-context';
import PropTypes from 'prop-types';
import StyledType from './type.styles';
import { StyledCacheWrapper } from '../../styles';
var TypeContext = /*#__PURE__*/createContext({
  variant: 'default',
  weight: 'normal',
  isItalic: false
});
var TypeProps = {
  /**
   * Add custom CSS classes to component.
   */
  className: PropTypes.string,
  /**
   * Content of the component
   */
  children: PropTypes.node,
  /**
   * Base color of text
   */
  variant: PropTypes.oneOf(['default', 'light', 'extraLight', 'dark', 'darker', 'brand', 'success', 'protection', 'community', 'information', 'critical', 'caution', 'inherit']),
  /**
   * Color of the emphasis
   */
  emphasis: PropTypes.oneOf(['brand', 'success', 'protection', 'community', 'information', 'critical', 'caution']),
  /**
   * Render the root with a semantic root element
   */
  element: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div', 'span', 'ul', 'ol', 'li']),
  /**
   * Whether type content is meant to be shown on a dark background
   */
  isInverse: PropTypes.oneOf(['auto', true, false]),
  /** Font weight */
  weight: PropTypes.oneOf(['thin', 'normal', 'semibold', 'bold']),
  /** Font style */
  decoration: PropTypes.oneOf(['underline', 'strike']),
  /** If true, text will change to italic */
  isItalic: PropTypes.bool,
  /** Text alignment */
  align: PropTypes.oneOf(['left', 'center', 'right']),
  /** Text transform */
  transform: PropTypes.oneOf(['uppercase', 'lowercase', 'capitalize', 'none']),
  /** Line height */
  lineHeight: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  /** Text truncate */
  isTruncated: PropTypes.bool,
  /** If false, element convert from block to inline */
  isBlock: PropTypes.bool,
  /** Add utility objects for styling */
  utils: PropTypes.object,
  /** Font size */
  size: PropTypes.oneOf(['2xs', 'xs', 's', 'sm', 'm', 'md', 'l', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl']),
  /** White Space options */
  whiteSpace: PropTypes.oneOf(['nowrap', 'pre', 'pre-wrap', 'pre-line', 'break-spaces', 'normal']),
  /** List should have bullets */
  hasBullets: PropTypes.bool
};
var defaultProps = {
  className: '',
  children: null,
  variant: undefined,
  element: 'div',
  emphasis: null,
  isInverse: 'auto',
  weight: undefined,
  decoration: null,
  isItalic: undefined,
  align: null,
  transform: null,
  lineHeight: null,
  isTruncated: false,
  isBlock: true,
  utils: {},
  size: null,
  whiteSpace: null,
  hasBullets: true
};

/**
 *
 * @ignore @description
 * ## Type Component
 * The Type component is for rendering basic text strings or elements with brand styles applied. Use this to facilitate text color and rendering accessible Page Headings.
 *
 * @ignore @see Mesh: [Type Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/visual-elements-type--default)
 *
 * @ignore @example
 * ```jsx
 * import { Type } from 'mesh-component-library';
 *
 * <Type {...props}>
 *  {children}
 * </Type>
 * ```
 */

var Type = function Type(props) {
  var children = props.children,
    className = props.className,
    variant = props.variant,
    element = props.element,
    utils = props.utils,
    isInverse = props.isInverse,
    hasBullets = props.hasBullets,
    weight = props.weight,
    isItalic = props.isItalic,
    other = _objectWithoutProperties(props, _excluded);
  var useIsInverse = React.useContext(InverseContext);
  var parentContext = useContext(TypeContext);
  variant = variant || parentContext.variant;
  weight = weight || parentContext.weight;
  isItalic = isItalic || parentContext.isItalic;
  var contextValue = useMemo(function () {
    return {
      variant: variant,
      weight: weight,
      isItalic: isItalic
    };
  }, []);
  if (isInverse !== 'auto') {
    useIsInverse = isInverse;
  }
  var _formatStrings = function formatStrings(_children) {
    return React.Children.map(_children, function (child) {
      if (typeof child === 'string') {
        // find numbers following a `$` symbol
        var re1 = /\$\d+(d+)?/gm;
        // split every 3 characters starting from end
        var re2 = /.{1,3}(?=(.{3})*$)/gm;
        // format currency values with commas
        return child.replace(re1, function (match) {
          return "$".concat(match.slice(1).match(re2).join(','));
        });
      }
      // recursively get content of fragments or HTML elements
      if (typeof (child === null || child === void 0 ? void 0 : child.type) === 'string' || (child === null || child === void 0 ? void 0 : child.type) === React.Fragment) {
        var _child$props;
        return /*#__PURE__*/React.cloneElement(child, null, _formatStrings(child === null || child === void 0 || (_child$props = child.props) === null || _child$props === void 0 ? void 0 : _child$props.children));
      }
      return child;
    });
  };
  var _useState = useState(_formatStrings(children)),
    _useState2 = _slicedToArray(_useState, 2),
    stateChildren = _useState2[0],
    setStateChildren = _useState2[1];
  var onRefSet = useCallback(function (node) {
    if (node && !!other.dangerouslySetInnerHTML) {
      node.innerHTML = _formatStrings(node.innerHTML);
    }
  });
  useEffect(function () {
    setStateChildren(_formatStrings(children));
  }, [children]);
  return /*#__PURE__*/React.createElement(StyledCacheWrapper, null, /*#__PURE__*/React.createElement(TypeContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(StyledType, _extends({
    as: element
  }, other, {
    variant: variant,
    isInverse: useIsInverse,
    utils: utils,
    element: element,
    className: className,
    hasBullets: hasBullets,
    weight: weight,
    isItalic: isItalic,
    ref: onRefSet
  }), stateChildren)));
};
Type.propTypes = TypeProps;
Type.defaultProps = defaultProps;
export default Type;